import React, { useEffect, useMemo, useState } from 'react';

import { Heading } from '../../components';
import { buildListingSeoCopy } from '../../util/listingSeoCopy';
import {
  enrichmentParamsFromPublicData,
  fetchListingEnrichment,
  getCachedListingEnrichment,
  setCachedListingEnrichment,
} from '../../util/listingEnrichmentClient';

import css from './ListingPage.module.css';

const LinkOrText = ({ href, className, children }) =>
  href ? (
    <a className={className} href={href}>
      {children}
    </a>
  ) : (
    <span className={className}>{children}</span>
  );

const SnapshotValue = ({ row }) => {
  if (row.linkPath) {
    return (
      <a className={css.seoAsideLink} href={row.linkPath}>
        {row.value}
      </a>
    );
  }
  return row.value;
};

/**
 * Transactional SEO copy for event listings.
 * Decision-support layout: breadcrumbs, intro, snapshot, about, planning links.
 * Nearby hub links render separately above the Location map.
 *
 * Hub hyperlinks are conditional on live enrichment at request time:
 * unlaunched city/venue/performer entities render as plain text; when those
 * hubs launch later, the next page view picks up the links automatically.
 */
const SectionListingSeoCopy = ({
  title,
  publicData,
  description = '',
  marketplaceName = 'PromoTix',
  enrichment: enrichmentProp = null,
}) => {
  const params = useMemo(() => enrichmentParamsFromPublicData(publicData), [publicData]);
  const cacheKey = params ? JSON.stringify(params) : null;
  const [enrichment, setEnrichment] = useState(() => {
    if (enrichmentProp) {
      return enrichmentProp;
    }
    return getCachedListingEnrichment(cacheKey);
  });

  useEffect(() => {
    if (enrichmentProp) {
      setEnrichment(enrichmentProp);
      return;
    }
    if (!cacheKey || typeof fetch !== 'function') {
      return;
    }
    const cached = getCachedListingEnrichment(cacheKey);
    if (cached) {
      setEnrichment(cached);
      return;
    }

    let cancelled = false;
    fetchListingEnrichment(params).then(body => {
      setCachedListingEnrichment(cacheKey, body);
      if (!cancelled) {
        setEnrichment(body);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, params, enrichmentProp]);

  const seo = useMemo(
    () =>
      buildListingSeoCopy({
        title,
        publicData,
        description,
        marketplaceName,
        enrichment,
      }),
    [title, publicData, description, marketplaceName, enrichment]
  );

  if (!seo?.introParagraph) {
    return null;
  }

  const venueName = seo.fields.venue;
  const snapshotRows = seo.eventSnapshot?.rows || [];
  const planningLinks = seo.planningLinks || [];
  const aboutPerformers = seo.performerLineup || [];
  const breadcrumbs = seo.breadcrumbs || [];
  // Only show breadcrumb trail when city or venue can resolve (linked or present for linking).
  const showBreadcrumbs = breadcrumbs.length > 1;

  return (
    <div className={css.sectionListingSeoCopy}>
      {showBreadcrumbs ? (
        <nav className={css.seoBreadcrumbs} aria-label="Breadcrumb">
          <ol className={css.seoBreadcrumbList}>
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              return (
                <li key={`${crumb.name}-${index}`} className={css.seoBreadcrumbItem}>
                  {index > 0 ? <span className={css.seoBreadcrumbSep}>/</span> : null}
                  {isLast && !crumb.path ? (
                    <span aria-current="page">{crumb.name}</span>
                  ) : crumb.path ? (
                    <a className={css.seoAsideLink} href={crumb.path}>
                      {crumb.name}
                    </a>
                  ) : (
                    <span aria-current={isLast ? 'page' : undefined}>{crumb.name}</span>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      ) : null}

      <p className={css.seoIntro}>{seo.introParagraph}</p>

      {snapshotRows.length ? (
        <div className={css.seoAsideBlock}>
          <Heading as="h2" rootClassName={css.sectionHeading}>
            {seo.eventSnapshot.heading}
          </Heading>
          <table className={css.seoSnapshotTable}>
            <tbody>
              {snapshotRows.map(row => (
                <tr key={row.label}>
                  <th scope="row">{row.label}</th>
                  <td>
                    <SnapshotValue row={row} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {seo.aboutParagraph ? (
        <div className={css.seoAsideBlock}>
          <Heading as="h2" rootClassName={css.sectionHeading}>
            {seo.aboutHeading}
          </Heading>
          <p className={css.seoAsideText}>{seo.aboutParagraph}</p>
          {aboutPerformers.some(p => p.path || p.eventsPath) ? (
            <ul className={css.seoPerformerHubList}>
              {aboutPerformers.map(p =>
                p.path || p.eventsPath ? (
                  <li key={p.name}>
                    {p.path ? (
                      <a className={css.seoAsideLink} href={p.path}>
                        {p.name}
                      </a>
                    ) : (
                      <span>{p.name}</span>
                    )}
                    {p.eventsPath ? (
                      <>
                        {' · '}
                        <a className={css.seoAsideLink} href={p.eventsPath}>
                          Upcoming events
                        </a>
                      </>
                    ) : null}
                  </li>
                ) : null
              )}
            </ul>
          ) : null}
        </div>
      ) : null}

      {planningLinks.length ? (
        <div className={css.seoAsideBlock}>
          <Heading as="h2" rootClassName={css.sectionHeading}>
            {seo.planningHeading}
          </Heading>
          <ul className={css.seoPlanningList}>
            {planningLinks.map(link => (
              <li key={link.slug || link.path}>
                <span className={css.seoPlanningCheck} aria-hidden="true">
                  ✓
                </span>
                <a className={css.seoAsideLink} href={link.path}>
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
          {seo.links.venuePath ? (
            <p className={css.seoAsideText}>
              <LinkOrText href={seo.links.venuePath} className={css.seoAsideLink}>
                {venueName ? `Full ${venueName} venue guide` : 'Full venue guide'}
              </LinkOrText>
            </p>
          ) : null}
        </div>
      ) : seo.links.venuePath ? (
        <div className={css.seoAsideBlock}>
          <Heading as="h2" rootClassName={css.sectionHeading}>
            {seo.planningHeading}
          </Heading>
          <p className={css.seoAsideText}>
            <LinkOrText href={seo.links.venuePath} className={css.seoAsideLink}>
              {seo.venueGuideCta}
            </LinkOrText>
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default SectionListingSeoCopy;
